import { Button, makeStyles } from '@material-ui/core';
import { navigate } from '@reach/router';
import clsx from 'clsx';
import React, { ChangeEvent, KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { generatePath } from 'react-router-dom';
import remarkGfm from 'remark-gfm';

import { useAuth } from '../../contexts/AuthContext/AuthContext';
import { initBenchwise } from '../../core/api/benchmark';
import { ROUTES } from '../../routes';
import { useErrorsDispatch } from '../FinancialData/shared/ErrorContext/ErrorContext';
import { ErrorContextActionTypes } from '../FinancialData/shared/ErrorContext/ErrorContext.types';

//import { api } from '../../../core/api';
//import { ROUTES } from '../../../routes';
//import { useParams } from '@reach/router';
//import { useErrorsDispatch } from '../../FinancialData/shared/ErrorContext/ErrorContext';
//import { ErrorContextActionTypes } from '../../FinancialData/shared/ErrorContext/ErrorContext.types';
//import ThemeBreadcrumbs from './BreadCrumbs';
//import { BreadCrumbLink } from './BreadCrumbs/BreadCrumbs';
// import GeneratedContent from './GeneratedContent';

interface Message {
  role: 'user' | 'bot' | 'error';
  content: string;
}

const BenchwisePage: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [input, setInput] = useState<string>('');
  const [content, setContent] = useState<string>(t('benchmark:benchwise.defaultContent') || '');
  const [showStudyReportHeader, setShowStudyReportHeader] = useState<boolean>(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [benchwiseEnabled, setBenchwiseEnabled] = useState<boolean>(false);
  const { user } = useAuth();
  const dispatchErrors = useErrorsDispatch();

  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const statuses = useMemo<string[]>(
    () => [
      'Analyzing your query',
      'Looking up data',
      'Analyzing industry',
      'Performing functional analysis',
      'Finding comparables',
      'Analyzing comparables',
      'Calculating margins',
      'Analyzing IQR',
      'Summarizing the results',
      'Generating appendices',
      'Compiling the final report',
    ],
    [],
  );
  const statusDurations = useMemo<number[]>(() => [2, 4, 5, 4, 4, 5, 4, 4, 6, 7, 10], []);
  const [tick, setTick] = useState<number>(0);

  const [statusIndex, setStatusIndex] = useState<number>(0);
  const [dots, setDots] = useState<string>('');

  useEffect(() => {
    setBenchwiseEnabled(true);
    if (loading) {
      const interval = setInterval(() => {
        setTick((prevTick) => prevTick + 1);

        let suml = statusDurations[0];
        let curIdx = statusDurations.length - 1;
        let curDuration = 0;
        for (let i = 1; i < statusDurations.length; i++) {
          if (tick < suml) {
            curIdx = i - 1;
            curDuration = tick - suml + statusDurations[i - 1];
            break;
          }
          suml += statusDurations[i];
        }

        if (curDuration === 0) {
          setDots('.');
        } else {
          setDots((prevDots) => (prevDots.length < 5 ? prevDots + '.' : prevDots));
        }

        setStatusIndex(curIdx);
      }, 2000);

      return () => clearInterval(interval);
    }
  }, [loading, tick, statuses.length, statusDurations, user]);

  useEffect(() => {
    setLoadingMessage(`${statuses[statusIndex]}${dots}`);
  }, [statusIndex, dots, statuses]);

  const handleSend = async () => {
    if (input.trim() === '') return;
    const userMessage: Message = { role: 'user', content: input };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setTick(0);
    setLoading(true);

    try {
      const data = await initBenchwise(input).catch((e) => {
        if (e?.data?.error === 'INSUFFICIENT_DOWNLOAD_CREDITS') {
          dispatchErrors({
            type: ErrorContextActionTypes.SetError,
            payload: { error: e, includeDetails: e.data.error === 'INSUFFICIENT_DOWNLOAD_CREDITS' },
          });
          setLoading(false);
          return null;
        }
        if (e?.data?.error === 'BENCHMARK_NOT_FOUND') {
          dispatchErrors({
            type: ErrorContextActionTypes.SetError,
            payload: { error: e, includeDetails: e.data.error === 'BENCHMARK_NOT_FOUND' },
          });
          setLoading(false);
          return null;
        }
        if (e?.data?.error === 'NO_RESULTS_FOUND') {
          const errorMessage: Message = {
            role: 'error',
            content:
              e?.data?.detail ||
              t('error:NO_RESULTS_FOUND') ||
              'No results were found matching your request.',
          };
          setMessages((prev) => [...prev, errorMessage]);
          setLoading(false);
          return null;
        }
        // Handle 429 Too Many Requests
        if (e?.data?.status === 429 || e?.status === 429) {
          const errorMsg =
            e?.data?.detail ||
            e?.data?.error ||
            e?.data?.message ||
            'Too many requests. Please wait a moment and try again.';
          const errorMessage: Message = {
            role: 'error',
            content: errorMsg,
          };
          setMessages((prev) => [...prev, errorMessage]);
          dispatchErrors({
            type: ErrorContextActionTypes.SetError,
            payload: { error: e },
          });
          setLoading(false);
          return null;
        }
        // Handle other HTTP errors
        if (e?.data || e?.status) {
          const errorMsg =
            e?.data?.detail ||
            e?.data?.error ||
            e?.data?.message ||
            'An error occurred. Please try again.';
          const errorMessage: Message = {
            role: 'error',
            content: errorMsg,
          };
          setMessages((prev) => [...prev, errorMessage]);
          dispatchErrors({
            type: ErrorContextActionTypes.SetError,
            payload: { error: e },
          });
          setLoading(false);
          return null;
        }
        // Re-throw unexpected errors to be caught by outer catch
        throw e;
      });

      if (data) {
        const botMessage: Message = {
          role: 'bot',
          content:
            'Please find the benchmarking study below. You will be redirected to the report shortly.',
        };
        setMessages((prev) => [...prev, botMessage]);
      }

      if (data && data.latestReport) {
        setShowStudyReportHeader(true);
        setContent(data.latestReport);
        // If the response contains benchmark_id, navigate immediatelly.
        if (data.benchmarkId) {
          setTimeout(() => {
            const newPath = generatePath(ROUTES.benchmarking.benchmarkTab, {
              benchmarkId: data.benchmarkId,
              tabName: 'benchwise',
            });
            navigate(newPath);
          }, 0);
        }
      }
    } catch (error) {
      console.error('Error fetching response:', error);
      const errorMessage: Message = {
        role: 'error',
        content: 'Unable to generate the study. Please try again.',
      };
      setMessages((prev) => [...prev, errorMessage]);

      // Also dispatch to error context for snackbar
      dispatchErrors({
        type: ErrorContextActionTypes.SetError,
        payload: { error },
      });
    }

    setLoading(false);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && benchwiseEnabled) {
      handleSend();
    }
  };

  return (
    <div className={classes.appContainer}>
      <div className={classes.chatContainer}>
        {messages.map((message, index) => (
          <div
            key={index}
            className={clsx(
              classes.message,
              message.role === 'user'
                ? classes.userMessage
                : message.role === 'error'
                ? classes.errorMessage
                : classes.botMessage,
            )}
          >
            {message.content}
          </div>
        ))}
        {loading && <div className={`${classes.message} bot`}>Loading...</div>}
      </div>
      <div className={classes.inputContainer}>
        <input
          className={classes.input}
          type="text"
          value={input}
          onChange={handleInputChange}
          placeholder="Type your study description here..."
          onKeyPress={handleKeyPress}
        />
        <Button
          className={classes.button}
          onClick={handleSend}
          disabled={!benchwiseEnabled || loading}
        >
          Generate
        </Button>
      </div>
      <div className={classes.container}>
        {loading ? (
          <div className={classes.statusContainer}>
            <div className={classes.statusItem}>{loadingMessage}</div>
          </div>
        ) : (
          <div className={classes.generatedContent}>
            {showStudyReportHeader && (
              <div className={classes.generatedHeader}>Benchmarking study report</div>
            )}
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  container: {
    maxWidth: '1000px',
    minWidth: '800px',
    width: '100%',
    padding: '20px',
    margin: 'auto',
    backgroundColor: '#fff',
    borderRadius: '8px',
    // boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  },
  statusContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    backgroundColor: '#e9ecef',
    borderRadius: '8px',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  },
  statusItem: {
    margin: '20px 0',
    padding: '10px 20px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    width: '100%',
    maxWidth: '600px',
    textAlign: 'center',
    fontSize: '16px',
    color: '#333',
  },
  button: {
    background: 'linear-gradient(90deg, #38d430 0%, #7ee8fa 30%, #4176fa 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 20px',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 400,
    letterSpacing: '1px',
    boxShadow: '0 2px 8px rgba(54, 209, 255, 0.10)',
    cursor: 'pointer',
    transition: 'background 0.3s, transform 0.3s',
    '&:hover': {
      background: 'linear-gradient(90deg, #2ebd3a 0%, #5ecbfa 50%, #345eea 100%)',
      transform: 'translateY(-2px)',
    },
    '&:active': {
      background: 'linear-gradient(90deg, #24992e 0%, #4bb3e6 50%, #274bbd 100%)',
      transform: 'translateY(0)',
    },
    '&:focus': {
      outline: 'none',
      boxShadow: '0 0 0 3px rgba(54, 209, 255, 0.3)',
    },
  },
  appContainer: {
    maxWidth: '1000px',
    minWidth: '800px',
    width: '100%',
    padding: '20px',
    margin: 'auto',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  },
  chatContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    flexGrow: 1,
    overflowY: 'auto',
    marginBottom: '10px',
    width: '100%',
  },
  message: {
    padding: '10px',
    marginBottom: '10px',
    borderRadius: '8px',
    maxWidth: '80%',
    minWidth: '70%',
    width: '100%',
  },
  userMessage: {
    alignSelf: 'flex-end',
    backgroundColor: '#dcf8c6',
  },
  botMessage: {
    alignSelf: 'flex-end',
    backgroundColor: '#f1f0f0',
    textAlign: 'right',
    marginLeft: 'auto',
  },
  errorMessage: {
    alignSelf: 'flex-end',
    backgroundColor: '#ffebee',
    border: '1px solid #ef5350',
    color: '#c62828',
    textAlign: 'right',
    marginLeft: 'auto',
    fontWeight: 500,
  },
  inputContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  generatedHeader: {
    background: 'linear-gradient(90deg, #4a90e2 0%, #38d430 100%)',
    borderRadius: '16px 16px 0 0',
    color: '#fff',
    fontSize: '2.2rem',
    fontWeight: 500,
    width: 'calc(100% + 40px)', // 2 * appContainer padding
    padding: '28px 0',
    margin: '0 0 0 -20px', // negative left margin to offset appContainer padding
    textAlign: 'center',
    letterSpacing: '0.5px',
    position: 'relative',
    left: 0,
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundImage: 'radial-gradient(rgba(128,128,128,0.13) 2px, transparent 2px)',
      backgroundSize: '8px 8px',
      borderRadius: '8px 8px 0 0',
      pointerEvents: 'none',
    },
  },
  generatedContent: {
    '& table': {
      borderCollapse: 'collapse',
      width: '100%',
      fontFamily: 'Arial, sans-serif',
      margin: '20px 0',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)' /* Added subtle shadow */,
    },

    '& thead th': {
      backgroundColor: '#4CAF50' /* Changed to a modern green color */,
      color: '#fff',
      padding: '12px 15px',
      textAlign: 'left',
    },

    '& tbody tr': {
      borderBottom: '1px solid #ddd' /* Added border for rows */,
    },

    '& tbody tr:nth-child(2n)': {
      backgroundColor: '#f9f9f9' /* Lighter alternate row color */,
    },

    '& tbody tr:nth-child(2n+1)': {
      backgroundColor: '#fff' /* White alternate row color */,
    },

    '& tbody tr:hover': {
      backgroundColor: '#f1f1f1' /* Highlight row on hover */,
    },

    '& tbody td': {
      padding: '12px 15px',
    },
  },
  input: {
    flex: 1,
    padding: '10px',
    borderRadius: '8px',
    border: '1px solid rgba(0, 0, 0, 0.1)',
    outline: 'none',
    transition: 'border-color 0.3s',
    '&:focus': {
      borderColor: '#007bff',
    },
  },
  sendButton: {
    backgroundColor: '#007bff',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '10px',
    fontSize: '16px',
    cursor: 'pointer',
    transition: 'background-color 0.3s, transform 0.3s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    '&:hover': {
      backgroundColor: '#0056b3',
      transform: 'translateY(-2px)',
    },
    '&:active': {
      backgroundColor: '#004494',
      transform: 'translateY(0)',
    },
    '&:focus': {
      outline: 'none',
      boxShadow: '0 0 0 3px rgba(0, 123, 255, 0.5)',
    },
  },
}));

export default BenchwisePage;
